import * as React from "react";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import { useMutation } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { object, string } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { resetPasswordFn } from "../APIServices/Services";
import { useNavigate } from "react-router-dom";
import Spinner from "../ui/Spinner";

const useStyles = makeStyles((themes) => ({
  resetPasswordButton: {
    backgroundColor: themes.palette.common.blue,
    "&:hover": {
      backgroundColor: themes.palette.common.blue,
    },
  },
}));

export default function ResetPassword({ history }) {
  const classes = useStyles();
  const theme = createTheme();

  const validateSchema = object().shape({
    email: string()
      .min(1, "Email address is required")
      .email("Email Address is invalid"),
  });

  const methods = useForm({
    resolver: yupResolver(validateSchema),
  });

  const {
    handleSubmit,
    register,
    reset,
    formState: { errors, isSubmitSuccessful },
  } = methods;

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset();
    }
  }, [reset, isSubmitSuccessful]);

  const navigate = useNavigate();

  const { mutate, isLoading, isError, error } = useMutation(
    (email) => resetPasswordFn(email),
    {
      onSuccess: (data) => {
        navigate("/password-reset-email")
      },
    }
  );

  const onSubmitHandler = (email) => {
    mutate(email);
  };

  return (
    <ThemeProvider theme={theme}>
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <Box
          sx={{
            marginTop: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <Typography component="h1" variant="h4">
            Password Reset
          </Typography>
          {isError && (
            <Typography component="h1" variant="h4">
              An error occurred: {error}
            </Typography>
          )}
          <Box
            component="form"
            onSubmit={handleSubmit(onSubmitHandler)}
            noValidate
            sx={{ mt: 1 }}
          >
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              {...register("email")}
              error={errors.username ? true : false}
              disabled={isLoading}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              className={classes.resetPasswordButton}
              disabled={isLoading}
            >
              Reset Password
            </Button>
            {isLoading && <Spinner />}
          </Box>
        </Box>
      </Container>
    </ThemeProvider>
  );
}
